import React, { Children, useState } from "react";
import classnames from "classnames";

import ArrowIcon from "components/Icons/arrows/ArrowIcon";
import ChevronIcon from "components/Icons/ChevronIcon";

type Props = {
  /**
   * Number of cards to show per slide on desktop
   */
  cardsPerSlide?: number;

  /**
   * Array of cards to display in the carousel;
   * No classes or attributed are needed
   */
  children: JSX.Element | JSX.Element[];

  /**
   * Optional className to apply to the wrapper `div`
   */
  className?: string;

  id?: string;

  /**
   * Optional width to apply to cards on mobile and
   */
  mobileCardWidth?: number;

  /**
   * What to show in the case of no children are provided;
   * Can be text or a full element;
   * Will display centered in the carousel
   */
  noResultsDisplay?: string | JSX.Element;

  /**
   * Optional class to apply to the "See more" card, such as `bg-white elevation-low`
   */
  seeMoreCardClass?: string;

  /**
   * Path used for all see more links/cards;
   * Will only show if there are multiple pages
   */
  seeMoreHref?: string;

  /**
   * Click handler used for all see more links/cards;
   * Can be used for event tracking callbacks;
   * Will only show if there are multiple pages
   */
  seeMoreOnClick?: (e: React.KeyboardEvent | React.MouseEvent) => void;

  /**
   * Text override for the see more buttons;
   * `seeMoreHref` or `seeMoreOnClick` must be passed to show these;
   * * Will only show if there are multiple pages
   */
  seeMoreText?: string;

  /**
   * Whether to show the "See more" card in the carousel
   */
  showSeeMoreCard?: boolean;

  /**
   * Displays under the header, can be text or a full element
   */
  subtitle?: string | JSX.Element;

  /**
   * Header for the carousel, can be text or a styled element
   */
  title?: string | JSX.Element;
};

const Carousel = ({
  cardsPerSlide = 4,
  children,
  className,
  mobileCardWidth = 250,
  noResultsDisplay,
  seeMoreCardClass,
  seeMoreHref,
  seeMoreOnClick,
  seeMoreText = "Show all",
  showSeeMoreCard,
  subtitle,
  title,
  ...others
}: Props) => {
  const [carouselPage, setCarouselPage] = useState(0);

  const cardCount =
    showSeeMoreCard && Children.count(children) > cardsPerSlide
      ? Children.count(children) + 1
      : Children.count(children);

  if (!cardCount && !noResultsDisplay) {
    return null;
  }

  const totalPages = Math.ceil(cardCount / cardsPerSlide);

  const handleNextClick = () => {
    setCarouselPage(carouselPage + 1);
  };

  const handlePrevClick = () => {
    setCarouselPage(carouselPage - 1);
  };

  /* List */
  // Translate 100% plus the margin for each page
  const listStyle = {
    transform: `translateX(calc(-${100 * carouselPage}% - ${
      12 * carouselPage
    }px))`,
  };

  // Desktop: calculate percentage width of the list for each card, minus the margins in between
  const cardWidthPercentage = (1 / cardsPerSlide) * 100;
  const cardMarginWidthShare = ((cardsPerSlide - 1) * 12) / cardsPerSlide;

  const titleElement = typeof title === "string" ? <h2>{title}</h2> : title;

  const subtitleElement =
    typeof subtitle === "string" ? <p>{subtitle}</p> : subtitle;

  const hasShowMoreCard =
    showSeeMoreCard && seeMoreHref && seeMoreText && totalPages > 1;

  return (
    <>
      <div data-testid="carousel" className={classnames(className)} {...others}>
        <div className="flex items-end justify-between">
          <div>
            {title && titleElement}
            {subtitle && subtitleElement}
          </div>

          <div className="hidden lg:flex text-green items-center">
            {seeMoreHref && seeMoreText && (
              <a
                className="text-green font-bold  underline"
                href={seeMoreHref}
                onClick={seeMoreOnClick}
                data-testid="carousel-see-more-link"
              >
                {seeMoreText}
              </a>
            )}
            {totalPages > 1 && (
              <span className="ml-xxl">
                <button
                  className={classnames(
                    "rounded-full bg-white elevation-low mr-lg",
                    {
                      "text-deep-green-40": carouselPage === 0,
                    }
                  )}
                  disabled={carouselPage === 0}
                  aria-label="See previous"
                  onClick={handlePrevClick}
                >
                  <ChevronIcon direction="left" />
                </button>
                <button
                  className={classnames("rounded-full bg-white elevation-low", {
                    "text-deep-green-40": carouselPage === totalPages - 1,
                  })}
                  disabled={carouselPage === totalPages - 1}
                  aria-label="See next"
                  onClick={handleNextClick}
                >
                  <ChevronIcon direction="right" />
                </button>
              </span>
            )}
          </div>
        </div>

        <div
          className="overflow-auto lg:overflow-hidden -mx-sm p-sm pb-md snap-x snap-mandatory scroll-px-sm"
          style={{ width: "calc(100% + 16px)" }}
        >
          {Children.count(children) ? (
            <ul
              className="inline-flex lg:flex -mr-sm lg:mr-none transition-transform carousel-list"
              style={listStyle}
            >
              {Children.map(children, (child, i) => (
                <li
                  className={classnames("flex-shrink-0 carousel__card mr-md", {
                    "snap-end":
                      !hasShowMoreCard && i === Children.count(children) - 1,
                    "snap-start":
                      hasShowMoreCard || i < Children.count(children) - 1,
                  })}
                >
                  {child}
                </li>
              ))}
              {hasShowMoreCard && (
                <li
                  className={classnames(
                    "flex-shrink-0 carousel__card snap-end",
                    seeMoreCardClass
                  )}
                >
                  <a
                    className="w-full h-full flex flex-col items-center justify-center bg-white"
                    href={seeMoreHref}
                    onClick={seeMoreOnClick}
                  >
                    <div className="bg-light-grey rounded-full p-xl mb-sm">
                      <ArrowIcon width="20" height="20" direction="right" />
                    </div>

                    <span className="font-bold">{seeMoreText}</span>
                  </a>
                </li>
              )}
            </ul>
          ) : (
            <div
              className="flex items-center justify-center w-full"
              style={{ minHeight: "100px" }}
            >
              {noResultsDisplay}
            </div>
          )}
        </div>

        {seeMoreHref && seeMoreText && (
          <a
            className="block lg:hidden text-green font-bold mt-xs cursor-pointer underline"
            href={seeMoreHref}
            onClick={seeMoreOnClick}
          >
            {seeMoreText}
          </a>
        )}
      </div>

      <style jsx>{`
        .carousel__card {
          width: ${mobileCardWidth}px;
        }

        @media only screen and (min-width: 1025px) {
          .carousel__card {
            width: calc(${cardWidthPercentage}% - ${cardMarginWidthShare}px);
          }
        }
      `}</style>
    </>
  );
};

export default Carousel;
